import { useCallback, useEffect, useState } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import * as braintree from 'braintree-web'
import { useDispatch, useSelector } from 'react-redux'

import { actionTypes } from 'actions/actionTypes'
import { PaymentProps } from 'routes/Checkout/Steps/Payment/Payment'
import {
  checkoutClearErrors,
  fireCheckoutError,
  setPayPalNonce,
} from 'routes/Checkout/checkoutActions'
import { CARD_DETAILS } from 'routes/Checkout/checkoutConfig'
import { getPreviewOrderId } from 'routes/Checkout/checkoutSelectors'

import { formatAddress } from '../utils/utils'
import { tokeniseCard } from './utils'

const initialState = {
  errorStatus: {
    showError: false,
    isEmpty: true,
  },
  isFormValid: false,
  isFormSubmitting: false,
}

const hostedFieldsOptions = {
  number: {
    container: `#${CARD_DETAILS.CARD_NUMBER}`,
    formatInput: true,
  },
  expirationDate: {
    container: `#${CARD_DETAILS.EXPIRY_DATE}`,
  },
  cvv: {
    container: `#${CARD_DETAILS.CVV}`,
  },
}

const hostedFieldStyles = {
  input: {
    color: '#516272',
    'font-family': 'AxiformaBook, Helvetica, sans-serif',
    'font-size': '16px',
    'line-height': '20px',
  },
}
type useHostedFieldsProps = Pick<
  PaymentProps,
  'braintreeClientInstance' | 'submitOrder' | 'threeDSecureInstance'
>

export const useHostedFields = ({
  braintreeClientInstance,
  submitOrder,
  threeDSecureInstance,
}: useHostedFieldsProps) => {
  const [hostedFieldsInstance, setHostedFieldsInstance] = useState<braintree.HostedFields | null>(
    null,
  )
  const [hostedFieldsFrameLoaded, setHostedFieldsFrameLoaded] = useState(false)
  const [inputCardNumberError, setInputCardNumberError] = useState(initialState.errorStatus)
  const [inputExpiryDateError, setInputExpiryDateError] = useState(initialState.errorStatus)
  const [inputCvvError, setInputCvvError] = useState(initialState.errorStatus)
  const [isFormValid, setIsFormValid] = useState<boolean>(initialState.isFormValid)
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(initialState.isFormSubmitting)
  const dispatch = useDispatch()
  const previewOrderID = useSelector(getPreviewOrderId)
  const handleCustomerCancelledThreeDSecure = useCallback(() => {
    if (!threeDSecureInstance) return
    dispatch(fireCheckoutError(actionTypes.CUSTOMER_CANCELLED_THREEDSECURE))
    datadogRum.addAction('checkout_payment_3ds_cancelled')
  }, [dispatch, threeDSecureInstance])

  const cancelVerification = useCallback(() => {
    if (!threeDSecureInstance) return

    threeDSecureInstance.cancelVerifyCard((err) => {
      if (err) {
        if (err instanceof Error) {
          datadogLogs.logger.error('Error during cancelVerifyCard', {
            message: err.message,
            stack: err.stack,
          })
        } else {
          datadogLogs.logger.error('Non-standard error during cancelVerifyCard', {
            error: err,
          })
        }
      } else {
        datadogLogs.logger.info('Cancel verification successful')
      }
    })
  }, [threeDSecureInstance])

  const handleLookUpComplete = useCallback(
    (data, next) => {
      if (data?.requiresUserAuthentication || data?.threeDSecureInfo?.liabilityShifted) {
        next()
      } else {
        dispatch(fireCheckoutError(actionTypes.CUSTOMER_NOT_ENROLLED_IN_THREEDSECURE))
        setIsFormSubmitting(false)
        cancelVerification()
      }
    },
    [cancelVerification, dispatch],
  )

  // when user submits the form
  const handleSubmit = async (options: Record<string, any>) => {
    dispatch(checkoutClearErrors())
    datadogRum.addAction('click_pay_with_card')
    if (!previewOrderID) {
      dispatch(fireCheckoutError(actionTypes.MISSING_PREVIEW_ORDER))

      return
    }
    if (!hostedFieldsInstance || !threeDSecureInstance) return
    setIsFormSubmitting(true)

    try {
      setIsFormSubmitting(true)

      const { payload, error } = await tokeniseCard(
        hostedFieldsInstance,
        options.values.cardHolderName,
      )

      if (!payload || !payload.nonce) {
        setIsFormSubmitting(false)
        // user can retry - dispatches error to show ui and passes error to datadog
        dispatch(fireCheckoutError(actionTypes.CARD_TOKENIZATION_FAILED, error))

        setIsFormSubmitting(false)

        return
      }

      threeDSecureInstance.on('lookup-complete', handleLookUpComplete)

      threeDSecureInstance.on('customer-canceled', handleCustomerCancelledThreeDSecure)

      const { deliveryAddress, values } = options
      const address = formatAddress(deliveryAddress)
      const formValues = values.isBillingAddressDifferent ? values : address

      const result = await threeDSecureInstance.verifyCard({
        nonce: payload.nonce,
        amount: '0.0' as any,
        challengeRequested: true,
        bin: payload.details.bin,
        additionalInformation: {
          acsWindowSize: '03',
        },
        billingAddress: {
          streetAddress: formValues.houseNo.substring(0, 50),
          postalCode: formValues.postcode,
          locality: formValues.town,
        },
      })

      const { threeDSecureInfo, nonce } = result
      const { liabilityShiftPossible, liabilityShifted } = threeDSecureInfo

      if (!liabilityShiftPossible) {
        dispatch(
          fireCheckoutError(
            actionTypes.VALID_CARD_DETAILS_NOT_PROVIDED,
            `liabilityShiftPossible:${liabilityShiftPossible}`,
          ),
        )

        return
      }

      if (!liabilityShifted) {
        dispatch(fireCheckoutError(actionTypes.SIGNUP_PAYMENT_FAILED))

        return
      }

      dispatch(setPayPalNonce(nonce))
      submitOrder()
    } catch (error) {
      dispatch(fireCheckoutError(actionTypes.VALID_CARD_DETAILS_NOT_PROVIDED, error))
    } finally {
      setIsFormSubmitting(false)
    }
  }

  // when user clicks submit without filling form details
  const handleCardDetailsValidation = () => {
    if (inputCardNumberError.isEmpty) {
      setInputCardNumberError({ ...inputCardNumberError, showError: true })
    }
    if (inputCvvError.isEmpty) {
      setInputCvvError({ ...inputCvvError, showError: true })
    }
    if (inputExpiryDateError.isEmpty) {
      setInputExpiryDateError({
        ...inputExpiryDateError,
        showError: true,
      })
    }
  }

  const handleOnValidityChanged = useCallback((event: any) => {
    const errorField = event.emittedBy
    const formValid = Object.keys(event.fields).every((key) => event.fields[key].isValid)
    setIsFormValid(formValid)

    const { isEmpty, isValid } = event.fields[errorField]
    switch (event.emittedBy) {
      case 'number':
        return setInputCardNumberError({
          showError: !isValid,
          isEmpty,
        })
      case 'expirationDate':
        return setInputExpiryDateError({
          showError: !isValid,
          isEmpty,
        })
      case 'cvv':
        return setInputCvvError({ showError: !isValid, isEmpty })
      default:
        return undefined
    }
  }, [])

  const initHostedFields = useCallback(async () => {
    const createHostedFieldInstance = async () => {
      if (!braintreeClientInstance) {
        datadogLogs.logger.warn(
          'Attempted to create hosted fields without Braintree client instance',
          {
            context: 'initHostedFields',
          },
        )

        return
      }
      const hostedField = await braintree.hostedFields.create({
        client: braintreeClientInstance,
        fields: hostedFieldsOptions,
        styles: hostedFieldStyles,
      })
      setHostedFieldsInstance(hostedField)
    }
    try {
      await createHostedFieldInstance()
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error')
      datadogLogs.logger.error('hostedFieldInstance failed to create', err)
    }
  }, [braintreeClientInstance])

  const initCardDetailsValidation = useCallback(async () => {
    if (!hostedFieldsInstance) return

    hostedFieldsInstance.on('validityChange', handleOnValidityChanged)
    hostedFieldsInstance.on('blur', handleOnValidityChanged)
  }, [handleOnValidityChanged, hostedFieldsInstance])

  // to initalise hostedfields client
  useEffect(() => {
    if (braintreeClientInstance && hostedFieldsFrameLoaded) {
      initHostedFields()
    }
  }, [braintreeClientInstance, initHostedFields, hostedFieldsFrameLoaded])

  // do card verification
  useEffect(() => {
    if (hostedFieldsInstance) {
      initCardDetailsValidation()
    }
  }, [hostedFieldsInstance, initCardDetailsValidation])

  useEffect(
    () => () => {
      if (hostedFieldsInstance) {
        hostedFieldsInstance.teardown()
        setHostedFieldsInstance(null)
      }
    },

    [hostedFieldsInstance],
  )

  return {
    formValidationError: {
      showCardNumberError: inputCardNumberError.showError,
      showCVVError: inputCvvError.showError,
      showExpiryDateError: inputExpiryDateError.showError,
    },
    isFormValid,
    handleCardDetailsValidation,
    handleOnValidityChanged,
    setHostedFieldsInstance,
    handleSubmit,
    isFormSubmitting,
    handleLookUpComplete,
    handleCustomerCancelledThreeDSecure,
    setHostedFieldsFrameLoaded,
    cancelVerification,
  }
}
